import React, { useState } from "react";

import { uniqBy } from "lodash";
import { CSVLink } from "react-csv";

import { useGamesContext } from "../contexts/Games";
import { CSVData } from "../types/games";

import { currencyFormat, defaultDateTimeFormat } from "../../../../lib/utils";
import { IGame } from "../../../../types/api";
import { useAppSelector } from "../../../../hooks/reduxHooks";
import { selectFields } from "../../../../features/global/globalSlice";

const DownloadButton = ({ site }: { site?: string }): React.JSX.Element => {
    const fields = useAppSelector(selectFields);
    const { games, umpirePay } = useGamesContext();

    const [csvData, setCSVData] = useState<Array<CSVData>>([]);
    const CSVHeaders = [
        { label: "Date", key: "date" },
        { label: "Age", key: "age" },
        { label: "League", key: "league" },
        { label: "Teams", key: "teams" },
        { label: "Location", key: "location" },
        { label: "Field #", key: "field" },
        { label: "Umpire 1", key: "ump1" },
        { label: "Umpire 1 Replacement Requested", key: "ump1replacement" },
        { label: "Umpire 2", key: "ump2" },
        { label: "Umpire 2 Replacement Requested", key: "ump2replacement" },
        { label: "Pay 1", key: "pay1" },
        { label: "Pay 2", key: "pay2" },
        { label: "Open", key: "open" },
    ];

    const generateCSVRow = (g: IGame): CSVData => {
        const ump1 = g.umpires?.find(u => u.location === "plate");
        const ump2 = g.umpires?.find(u => u.location === "field");

        const ump1Pay = umpirePay
            .find(u => u.ageLevel === g.ageLevel.ageLevel)
            ?.umpires.find(
                u => u.title === "Umpire" || u.title === "Plate Umpire",
            )?.pay;
        const ump2Pay = umpirePay
            .find(u => u.ageLevel === g.ageLevel.ageLevel)
            ?.umpires.find(u => u.title === "Field Umpire")?.pay;
        const numUmps = umpirePay.find(u => u.ageLevel === g.ageLevel.ageLevel)
            ?.umpires.length;

        const uniqueUmps = uniqBy(g.umpires, "id");
        let openings: number = 0;
        if (numUmps) {
            if (uniqueUmps?.some(u => u.replacement === 1)) {
                openings =
                    numUmps -
                    (uniqueUmps.filter(u => u.replacement !== 1).length || 0);
            } else {
                openings = numUmps - (uniqueUmps?.length || 0);
            }
        } else {
            openings = 0;
        }

        return {
            date: defaultDateTimeFormat.format(new Date(g.date)),
            age: g.ageLevel.ageLevel,
            league: g.leagueName,
            teams: `${g.visitor.name || "TBD"} @ ${g.home.name || "TBD"}`,
            location: g.fieldName,
            field: g.diamond
                ? fields
                      .find(f => f.id === g.fieldId)
                      ?.diamonds.find(d => d.id === g.diamond)?.name
                : undefined,
            ump1: ump1 ? ump1.name : "Available",
            ump1replacement: ump1?.replacement === 1 ? "Yes" : undefined,
            ump2: ump2
                ? ump2.name
                : numUmps && numUmps > 1
                  ? "Available"
                  : undefined,
            ump2replacement: ump2?.replacement === 1 ? "Yes" : undefined,
            pay1: ump1Pay && currencyFormat.format(parseInt(ump1Pay)),
            pay2: ump2Pay && currencyFormat.format(parseInt(ump2Pay)),
            open: openings,
        };
    };

    const handleDownload = () => {
        const CSVData = games.map(g => generateCSVRow(g));
        setCSVData(CSVData);
    };

    return (
        <CSVLink
            data={csvData}
            headers={CSVHeaders}
            filename={`${site}-UmpHub.${new Date().getTime()}.csv`}
            className="btn btn-primary"
            target="_blank"
            onClick={handleDownload}
        >
            Download Excel File
        </CSVLink>
    );
};

export default DownloadButton;
